import { useMemo } from 'react';

export type MultipleLoadingResult = {
    isAnyLoading: boolean;
    isAllLoading: boolean;
    getLoadingFor: (key: string) => boolean;
    showSkeletonWhen: (keys: string[]) => boolean;
    loadingCount: number;
    loadingKeys: string[];
};

export type MultipleLoadingStates = {
    loadingStates: Record<string, boolean>;
};

/**
 * Hook pour gérer plusieurs états de loading simultanés
 * Fournit des utilitaires pour vérifier l'état de chargement de différentes ressources
 *
 * @param params - Configuration des états de loading
 * @param params.loadingStates - Objet contenant les états de loading par clé
 * @returns Objet avec les utilitaires de gestion des états multiples
 *
 * @example
 * ```tsx
 * const { isAnyLoading, showSkeletonWhen, getLoadingFor } = useMultipleLoading({
 *   loadingStates: {
 *     user: userLoading,
 *     data: dataLoading,
 *     settings: settingsLoading
 *   }
 * });
 *
 * return (
 *   <div>
 *     {showSkeletonWhen(['user', 'data']) && <UserDataSkeleton />}
 *     {getLoadingFor('settings') && <SettingsSkeleton />}
 *     {!isAnyLoading && <MyContent />}
 *   </div>
 * );
 * ```
 */
export const useMultipleLoading = ({
    loadingStates,
}: MultipleLoadingStates): MultipleLoadingResult =>
    useMemo(() => {
        const loadingKeys = Object.keys(loadingStates).filter((key) => loadingStates[key]);
        const totalKeys = Object.keys(loadingStates).length;

        return {
            isAnyLoading: loadingKeys.length > 0,
            isAllLoading: totalKeys > 0 && loadingKeys.length === totalKeys,
            getLoadingFor: (key: string) => loadingStates[key] ?? false,
            showSkeletonWhen: (keys: string[]) => keys.some((key) => loadingStates[key]),
            loadingCount: loadingKeys.length,
            loadingKeys,
        };
    }, [loadingStates]);
